import React, { useEffect, useId, useRef, useState } from "react";
import { X, ArrowUpRight } from "lucide-react";
import "./banners.css";

export function safeBannerUrl(value) {
  if (typeof value === "string") value = value.trim();
  if (typeof value !== "string" || /[\s\\]/.test(value) || Array.from(value).some((char) => char.charCodeAt(0) < 32)) return "";
  if (value.startsWith("/") && !value.startsWith("//")) return value;
  try { return new URL(value).protocol === "https:" ? value : ""; } catch { return ""; }
}

function BannerImage({ src, preview, onStatus }) {
  const [status, setStatus] = useState("loading");
  const callback = useRef(onStatus);
  callback.current = onStatus;
  useEffect(() => { callback.current?.(src, "loading"); }, [src]);
  function report(next) { setStatus(next); callback.current?.(src, next); }
  return <>
    <img className="nb-image" src={src} alt="" onLoad={() => report("loaded")} onError={() => report("error")} style={status === "error" ? { display: "none" } : undefined} />
    {preview && status === "error" && <p role="alert" className="m-4 rounded-xl bg-amber-50 p-4 text-sm text-amber-900">Immagine non caricata. Usa un link HTTPS diretto al file immagine, accessibile senza login; i link a pagine di condivisione o scaduti non funzionano.</p>}
  </>;
}

export default function BannerCard({ banner, onClose, preview = false, onImageStatus }) {
  const titleId = useId();
  const link = safeBannerUrl(banner.cta_url);
  const image = safeBannerUrl(banner.image_url);
  const theme = ["ocean", "violet", "emerald", "amber"].includes(banner.theme) ? banner.theme : "ocean";
  return (
    <article className={`nb-card nb-${theme}`} aria-labelledby={titleId}>
      <div className="nb-accent" aria-hidden="true" />
      <button type="button" className="nb-close" aria-label="Chiudi banner" onClick={onClose}><X size={20} /></button>
      {image && <BannerImage key={image} src={image} preview={preview} onStatus={onImageStatus} />}
      <div className="nb-copy">
        <p className="nb-eyebrow">{banner.eyebrow || "Norma • Novità per te"}</p>
        <h2 id={titleId}>{banner.title || "Il tuo prossimo messaggio"}</h2>
        <p className="nb-body">{banner.body || "Scrivi il messaggio o lascia che l’AI ti aiuti a creare il banner."}</p>
        <div className="nb-actions">
          {link && banner.cta_label && (preview
            ? <button type="button" className="nb-cta" onClick={() => window.open(link, "_blank", "noopener,noreferrer")}>{banner.cta_label}<ArrowUpRight size={18} /></button>
            : <a className="nb-cta" href={link} onClick={onClose}>{banner.cta_label}<ArrowUpRight size={18} /></a>)}
          <button type="button" className="nb-later" onClick={onClose}>Continua nell’app</button>
        </div>
      </div>
    </article>
  );
}

export function BannerModal({ banner, onClose, preview = false }) {
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    ref.current?.querySelector("button")?.focus();
    const key = (event) => {
      if (event.key === "Escape") closeRef.current();
      if (event.key !== "Tab") return;
      const buttons = ref.current?.querySelectorAll("button,a[href]");
      if (!buttons?.length) return;
      const first = buttons[0], last = buttons[buttons.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", key);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", key);
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  return <div ref={ref} className="nb-overlay" role="dialog" aria-modal="true" aria-label={preview ? "Test banner, visibile solo a te" : "Comunicazione Norma"}>
    <BannerCard banner={banner} onClose={onClose} preview={preview} />
  </div>;
}
